import Button from 'components/button';
import Input from 'components/input';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import request from 'request';
import styles from './create-modal.module.scss';

const Modal = ({ onClose }: { onClose: () => void }) => {
	const [sum, setSum] = React.useState('');
	const [interestRate, setInterestRate] = React.useState('');
	const [duration, setDuration] = React.useState('');
	const [numberOfGuarantors, setNumberOfGuarantors] = React.useState('');
	const [numberOfPayments, setNumberOfPayments] = React.useState('');
	const [priorLoans, setPriorLoans] = React.useState('');
	const [loanDurationOfCurrentLoan, setLoanDurationOfCurrentLoan] = React.useState('');
	const [repeatedClient, setRepeatedClient] = React.useState({
		key: '',
		value: '',
	});
	const [activeLoans, setActiveLoans] = React.useState({
		key: '',
		value: '',
	});

	const [pending, setPending] = React.useState(false);
	const [validError, setValidError] = React.useState<
		{
			name:
				| 'inn'
				| 'sum'
				| 'interestRate'
				| 'duration'
				| 'numberOfGuarantors'
				| 'numberOfPayments'
				| 'priorLoans'
				| 'repeatedClient'
				| 'loanDurationOfCurrentLoan'
				| 'activeLoans';
			value: string;
		}[]
	>([]);

	const navigate = useNavigate();

	useEffect(() => {
		if (repeatedClient.key === 'new' || activeLoans.key === 'No') {
			setLoanDurationOfCurrentLoan('0');
			setNumberOfPayments('0');
		} else if (
			(repeatedClient.key === 'repeated' || activeLoans.key === 'yes') &&
			loanDurationOfCurrentLoan === '0'
		) {
			setLoanDurationOfCurrentLoan('1');
		}
	}, [repeatedClient.key, activeLoans.key]);

	const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		let validationError: typeof validError = [];
		if (sum.trim().length === 0) {
			validationError?.push({
				name: 'sum',
				value: 'Amount is required',
			});
		}
		if (interestRate.trim().length === 0) {
			validationError?.push({
				name: 'interestRate',
				value: 'Interest rate is required',
			});
		}
		if (duration.trim().length === 0) {
			validationError?.push({
				name: 'duration',
				value: 'Duration is required',
			});
		}
		if (numberOfGuarantors.trim().length === 0) {
			validationError?.push({
				name: 'numberOfGuarantors',
				value: 'Number of guarantors is required',
			});
		}
		if (numberOfPayments.trim().length === 0) {
			validationError?.push({
				name: 'numberOfPayments',
				value: 'Number of payments is required',
			});
		}
		if (repeatedClient.key.trim().length === 0) {
			validationError?.push({
				name: 'repeatedClient',
				value: 'Repeated client is required',
			});
		}
		if (loanDurationOfCurrentLoan.trim().length === 0) {
			validationError?.push({
				name: 'loanDurationOfCurrentLoan',
				value: 'Loan duration of current loan is required',
			});
		}
		if (priorLoans.trim().length === 0) {
			validationError?.push({
				name: 'priorLoans',
				value: 'Prior loans is required',
			});
		}
		if (activeLoans.key.trim().length === 0) {
			validationError?.push({
				name: 'activeLoans',
				value: 'Active loans is required',
			});
		}

		setValidError(validationError);

		if (validationError.length > 0) return;

		setPending(true);
		request
			.post('/applications', {
				amount: Number(sum),
				interest_rate: Number(interestRate),
				duration: Number(duration),
				number_of_guarantors: Number(numberOfGuarantors),
				number_of_payments: Number(numberOfPayments),
				prior_loans: Number(priorLoans),
				loan_duration_of_current_loan: Number(loanDurationOfCurrentLoan),
				active_loans_credit_cards: activeLoans.key,
				repeated_client: repeatedClient.key,
			})
			.then((res) => res.data)
			.then((data) => {
				navigate(`application/${data.id}/ocr`);
			})
			.finally(() => setPending(false));
	};

	return (
		<div className={styles.modal} onMouseDown={onClose}>
			<form
				className={styles.modalWrap}
				onMouseDown={(event) => event.stopPropagation()}
				onSubmit={handleCreate}
			>
				<div className={styles.title}>New application</div>
				<div className={styles.inputs}>
					<div className={styles.input}>
						<Input
							title="Amount"
							type="integer"
							value={sum}
							onInput={(value) => setSum(value)}
							step={100}
							min={100}
							max={12000}
							unit="JOD"
						/>
						{validError.find((error) => error.name === 'sum')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'sum')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Interest rate (annual)"
							type="float"
							value={interestRate}
							onInput={(value) => setInterestRate(value)}
							step={1}
							min={12}
							max={37.6}
							unit="%"
						/>
						{validError.find((error) => error.name === 'interestRate')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'interestRate')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Duration"
							type="integer"
							value={duration}
							onInput={(value) => setDuration(value)}
							step={1}
							min={8}
							max={40}
							unit="mon."
						/>
						{validError.find((error) => error.name === 'duration')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'duration')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Number of guarantors"
							type="integer"
							value={numberOfGuarantors}
							onInput={(value) => setNumberOfGuarantors(value)}
							step={1}
							min={1}
							max={3}
						/>
						{validError.find((error) => error.name === 'numberOfGuarantors')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'numberOfGuarantors')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Number of payments"
							type="integer"
							value={numberOfPayments}
							onInput={(value) => setNumberOfPayments(value)}
							step={1}
							min={0}
							disabled={repeatedClient.key === 'new' || activeLoans.key === 'No'}
							max={10000}
						/>
						{validError.find((error) => error.name === 'numberOfPayments')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'numberOfPayments')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Repeated client?"
							id="repeatedClient"
							value={repeatedClient.key}
							type="select"
							options={[
								{
									key: 'repeated',
									value: 'Repeated',
								},
								{
									key: 'new',
									value: 'New',
								},
							]}
							onSelect={(option) => {
								if (option.key === 'new') {
									setPriorLoans('1');
									setActiveLoans({ key: 'No', value: 'No' });
								}
								setRepeatedClient(option);
							}}
						/>
						{validError.find((error) => error.name === 'repeatedClient')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'repeatedClient')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Loan duration of current loan"
							value={loanDurationOfCurrentLoan}
							min={1}
							max={1000}
							step={1}
							type="integer"
							disabled={repeatedClient.key === 'new' || activeLoans.key === 'No'}
							onInput={(value) => setLoanDurationOfCurrentLoan(value)}
						/>
						{validError.find((error) => error.name === 'loanDurationOfCurrentLoan')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'loanDurationOfCurrentLoan')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Prior loans"
							value={priorLoans}
							min={0}
							max={18}
							step={1}
							type="integer"
							disabled={repeatedClient.key === 'new'}
							onInput={(value) => setPriorLoans(value)}
						/>
						{validError.find((error) => error.name === 'priorLoans')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'priorLoans')?.value}
							</div>
						)}
					</div>
					<div className={styles.input}>
						<Input
							title="Active loans"
							value={activeLoans.key}
							type="select"
							disabled={repeatedClient.key === 'new'}
							options={[
								{ key: 'Yes', value: 'Yes' },
								{ key: 'No', value: 'No' },
							]}
							onSelect={(option) => setActiveLoans(option)}
						/>
						{validError.find((error) => error.name === 'activeLoans')?.value && (
							<div className={styles.inputError}>
								{validError.find((error) => error.name === 'activeLoans')?.value}
							</div>
						)}
					</div>
				</div>
				<div className={styles.buttonGroup}>
					<Button color="orange" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" color="green" pending={pending}>
						Create
					</Button>
				</div>
			</form>
		</div>
	);
};

export default Modal;
